import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import SalesOrders from "./pages/SalesOrders";
import Inventory from "./pages/Inventory";
import PackingList from "./pages/PackingList";
import StockReport from "./pages/StockReport";
import JobCard from "./pages/JobCard";
import JobCardList from "./pages/JobCardList";
import WorkflowConfig from "./pages/WorkflowConfig";
import Specifications from "./pages/Specifications";
import TaskQueue from "./pages/TaskQueue";
import Users from "./pages/admin/Users";
import Roles from "./pages/admin/Roles";
import License from "./pages/admin/License";
import Customers from "./pages/purchase/Customers";
import Vendors from "./pages/purchase/Vendors";
import Departments from "./pages/masters/Departments";
import Shifts from "./pages/masters/Shifts";
import Plants from "./pages/masters/Plants";
import Units from "./pages/masters/Units";
import MaterialIndents from "./pages/purchase/MaterialIndents";
import PurchaseOrders from "./pages/purchase/PurchaseOrders";
import MRN from "./pages/purchase/MRN";
import QCInspection from "./pages/purchase/QCInspection";
import DebitNotes from "./pages/purchase/DebitNotes";
import TallyDashboard from "./pages/tally/TallyDashboard";
import TallyConfig from "./pages/tally/TallyConfig";
import OutstandingDashboard from "./pages/tally/OutstandingDashboard";
import CustomFields from "./pages/admin/CustomFields";
import CurrencySettings from "./pages/admin/CurrencySettings";
// Production Module
import ProductionOrders from "./pages/production/ProductionOrders";
import LiveDashboard from "./pages/production/LiveDashboard";
import ProductionEntry from "./pages/production/ProductionEntry";
import DowntimeEntry from "./pages/production/DowntimeEntry";
import Machines from "./pages/production/Machines";
import BOM from "./pages/production/BOM";
import RoutingMaster from "./pages/production/RoutingMaster";
import CycleTimes from "./pages/production/CycleTimes";
import DowntimeReasons from "./pages/production/DowntimeReasons";
import RejectionReasons from "./pages/production/RejectionReasons";
import DailySummary from "./pages/production/DailySummary";
import SupervisorDashboard from "./pages/production/SupervisorDashboard";
import ProductionSettings from "./pages/production/ProductionSettings";
import Dispatch from "./pages/production/Dispatch";
import ProcessMaster from "./pages/production/ProcessMaster";
import MaterialRequirements from "./pages/production/MaterialRequirements";

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: '20px' }}>Loading...</div>;
  return user ? <Layout>{children}</Layout> : <Navigate to="/login" />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/sales-orders" element={<PrivateRoute><SalesOrders /></PrivateRoute>} />
          <Route path="/sales-orders/job-cards/:soId" element={<PrivateRoute><JobCardList /></PrivateRoute>} />
          <Route path="/sales-orders/job-card/:soId/:itemIndex" element={<PrivateRoute><JobCard /></PrivateRoute>} />
          <Route path="/inventory" element={<PrivateRoute><Inventory /></PrivateRoute>} />
          <Route path="/packing" element={<PrivateRoute><PackingList /></PrivateRoute>} />
          <Route path="/stock-report" element={<PrivateRoute><StockReport /></PrivateRoute>} />
          <Route path="/workflow" element={<PrivateRoute><WorkflowConfig /></PrivateRoute>} />
          <Route path="/specifications" element={<PrivateRoute><Specifications /></PrivateRoute>} />
          <Route path="/task-queue" element={<PrivateRoute><TaskQueue /></PrivateRoute>} />
          {/* Purchase */}
          <Route path="/purchase/customers" element={<PrivateRoute><Customers /></PrivateRoute>} />
          <Route path="/purchase/vendors" element={<PrivateRoute><Vendors /></PrivateRoute>} />
          <Route path="/purchase/indents" element={<PrivateRoute><MaterialIndents /></PrivateRoute>} />
          <Route path="/purchase/orders" element={<PrivateRoute><PurchaseOrders /></PrivateRoute>} />
          <Route path="/purchase/mrn" element={<PrivateRoute><MRN /></PrivateRoute>} />
          <Route path="/purchase/qc" element={<PrivateRoute><QCInspection /></PrivateRoute>} />
          <Route path="/purchase/debit-notes" element={<PrivateRoute><DebitNotes /></PrivateRoute>} />
          {/* Masters */}
          <Route path="/masters/customers" element={<PrivateRoute><Customers /></PrivateRoute>} />
          <Route path="/masters/vendors" element={<PrivateRoute><Vendors /></PrivateRoute>} />
          <Route path="/masters/departments" element={<PrivateRoute><Departments /></PrivateRoute>} />
          <Route path="/masters/shifts" element={<PrivateRoute><Shifts /></PrivateRoute>} />
          <Route path="/masters/plants" element={<PrivateRoute><Plants /></PrivateRoute>} />
          <Route path="/masters/units" element={<PrivateRoute><Units /></PrivateRoute>} />
          {/* Production Module */}
          <Route path="/production/orders" element={<PrivateRoute><ProductionOrders /></PrivateRoute>} />
          <Route path="/production/live-dashboard" element={<PrivateRoute><LiveDashboard /></PrivateRoute>} />
          <Route path="/production/entry" element={<PrivateRoute><ProductionEntry /></PrivateRoute>} />
          <Route path="/production/downtime" element={<PrivateRoute><DowntimeEntry /></PrivateRoute>} />
          <Route path="/production/machines" element={<PrivateRoute><Machines /></PrivateRoute>} />
          <Route path="/production/bom" element={<PrivateRoute><BOM /></PrivateRoute>} />
          <Route path="/production/routing" element={<PrivateRoute><RoutingMaster /></PrivateRoute>} />
          <Route path="/production/process-master" element={<PrivateRoute><ProcessMaster /></PrivateRoute>} />
          <Route path="/production/material-requirements" element={<PrivateRoute><MaterialRequirements /></PrivateRoute>} />
          <Route path="/production/cycle-times" element={<PrivateRoute><CycleTimes /></PrivateRoute>} />
          <Route path="/production/downtime-reasons" element={<PrivateRoute><DowntimeReasons /></PrivateRoute>} />
          <Route path="/production/rejection-reasons" element={<PrivateRoute><RejectionReasons /></PrivateRoute>} />
          <Route path="/production/daily-summary" element={<PrivateRoute><DailySummary /></PrivateRoute>} />
          <Route path="/production/supervisor" element={<PrivateRoute><SupervisorDashboard /></PrivateRoute>} />
          <Route path="/production/settings" element={<PrivateRoute><ProductionSettings /></PrivateRoute>} />
          <Route path="/production/dispatch" element={<PrivateRoute><Dispatch /></PrivateRoute>} />
          {/* Tally */}
          <Route path="/tally/dashboard" element={<PrivateRoute><TallyDashboard /></PrivateRoute>} />
          <Route path="/tally/config" element={<PrivateRoute><TallyConfig /></PrivateRoute>} />
          <Route path="/tally/outstanding" element={<PrivateRoute><OutstandingDashboard /></PrivateRoute>} />
          {/* Admin */}
          <Route path="/admin/users" element={<PrivateRoute><Users /></PrivateRoute>} />
          <Route path="/admin/roles" element={<PrivateRoute><Roles /></PrivateRoute>} />
          <Route path="/admin/custom-fields" element={<PrivateRoute><CustomFields /></PrivateRoute>} />
          <Route path="/admin/currency" element={<PrivateRoute><CurrencySettings /></PrivateRoute>} />
          <Route path="/admin/license" element={<PrivateRoute><License /></PrivateRoute>} />
          {/* Default */}
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
